<template>
  <div>
    <div v-if="loading" class="section">
      <div class="container main has-text-centered">
        <div class="title is-4 has-text-primary">
          Finding rooms and availability...
        </div>
        <div>
          <i class="fas fa-gear has-text-primary fa-3x fa-spin"></i>
        </div>
      </div>
    </div>

    <div v-else>
      <div class="container main" style="max-width: 1152px">
        <div class="sticky-top">
          <div
            @click="backNavigation"
            class="has-text-link is-clickable is-size-7 back-navigation has-background-primary p-2 mb-4 has-text-white"
          >
            <i class="fas fa-arrow-left"></i> Back to search
          </div>
        </div>

        <!-- Hotel Name and Address -->
        <div class="address-box my-5">
          <h2 class="is-size-4 has-text-weight-bold">
            {{ hotelName }}
          </h2>
          <div v-if="hotelAddress">
            <div
              v-if="hotelAddress.chainName"
              class="is-7 has-text-weight-semibold"
            >
              by
              <span class=" is-uppercase">{{ hotelAddress.chainName }}</span>
            </div>
            <div class="is-capitalized">
              {{ hotelAddress.addressLine }}
            </div>
            <div class=" is-capitalized">
              {{ hotelAddress.city }} {{ hotelAddress.state }}
              {{ hotelAddress.postalCode }}
            </div>
          </div>

          <div
            class="is-size-7 has-text-primary is-clickable mt-1"
            @click="showMapModal = true"
          >
            View on map <i class="fa-solid fa-location-dot"></i>
          </div>

          <b-modal
            v-model="showMapModal"
            :render-on-mounted="true"
            has-modal-card
          >
            <div class="card">
              <header class="card-header">
                <p class="card-header-title">{{ hotelName }}</p>
              </header>
              <div class="card-content">
                <GMap
                  :lat="parseFloat(hotel.geocode.Latitude) / 100000"
                  :long="parseFloat(hotel.geocode.Longitude) / 100000"
                  width="600"
                  height="500"
                  carOrHotel="home"
                  :zoom="15"
                />
              </div>
            </div>
          </b-modal>
        </div>

        <!-- Hotel Property Images -->
        <HotelImages v-if="detailImages.length > 2" :images="detailImages" />

        <!-- Search Bar -->
        <div
          v-if="!pathMixin_isMeeting && !loadingRooms"
          class="box mt-5 mb-6"
          style="background-color: rgb(238 242 255);border: 1px solid rgba(209, 213, 219, 0.75)"
        >
          <div class="columns is-multiline is-vcentered">
            <div class="column is-full-mobile is-half-tablet">
              <div class="field">
                <label class="label is-small">Hotel Property</label>
                <div class="control has-icons-left">
                  <input
                    class="input has-background-light"
                    type="text"
                    :value="hotelName"
                    readonly
                  />
                  <span class="icon is-small is-left">
                    <i class="fas fa-map-marker-alt"></i>
                  </span>
                </div>
              </div>
            </div>

            <div class="column is-full-mobile is-one-quarter-tablet">
              <label class="label is-small">Check-in and Check-out</label>
              <DateSelectorRange
                v-if="newSearchDates.length > 0"
                segmentType="hotel"
                class="mr-1"
                v-model="newSearchDates"
                data-test="date_range_selector"
              />
            </div>

            <div class="column is-full-mobile is-one-quarter-tablet">
              <label class="label is-small">&nbsp;</label>
              <button class="button is-info is-fullwidth" @click="getLiveRates">
                Update Search
              </button>
            </div>
          </div>
        </div>

        <!-- Government Rate Message -->
        <div v-if="governmentRate" class="box government-rate-notification">
          <div class="is-flex is-align-items-center">
            <span class="icon has-text-success mr-2">
              <i class="fas fa-check-circle"></i>
            </span>
            <p class="has-text-grey-dark is-size-6">
              Federal per diem lodging rate for <span class="has-text-weight-medium">{{ hotelAddress.city }}</span>: 
              <span class="rate-amount">${{ governmentRate }} / night</span>
            </p>
          </div>
        </div>


        <!-- Hotel Rooms -->
        <div v-if="loadingRooms">
          <div class="section has-text-centered">
            <h3 class="title is-4 has-text-primary">
              Checking hotel rates and availability...
            </h3>
            <div>
              <i class="fas fa-gear has-text-primary fa-3x fa-spin"></i>
            </div>
          </div>
          <div class="columns is-multiline">
            <div class="column is-one-third-tablet">
              <RoomSkeleton />
            </div>
            <div class="column is-one-third-tablet">
              <RoomSkeleton />
            </div>
            <div class="column is-one-third-tablet">
              <RoomSkeleton />
            </div>
          </div>
        </div>

        <div v-else>
          <div v-if="filteredRooms.length > 0">
            <h3 class="title is-4 mt-6 mb-4">Choose Your Room:</h3>

            <div class="columns is-multiline">
              <div
                v-for="room in filteredRooms"
                :key="room.key"
                class="column is-full-mobile is-half-tablet is-one-third-desktop"
              >
                <StayRoom
                  v-if="room.rates.length > 0"
                  :room="room"
                  :hotel="hotel"
                  :sessionKey="sessionKey"
                />
              </div>
            </div>
          </div>
          <div v-else-if="filteredRooms.length == 0" class="has-text-centered">
            <b-message type="is-danger" has-icon>
              <span class="has-text-weight-medium"
                >No rooms found for the dates provided.</span
              >
            </b-message>
          </div>
        </div>

        <div v-if="amenities.length > 0">
          <h3 class="title is-4 mt-6 mb-4">Hotel Amenities:</h3>
          <div class="columns is-multiline">
            <div
              v-for="(amenity, index) in description.amenities.slice(
                0,
                description.amenities.length / 2
              )"
              :key="index"
              class="column is-half-tablet is-one-quarter-desktop"
            >
              <span class="icon-text">
                <span class="icon has-text-primary">
                  <i class="fas fa-check"></i>
                </span>
                <span class="is-size-6">{{ amenity }}</span>
              </span>
            </div>
          </div>
        </div>

        <div v-if="policy.checkInTime" class="content mt-6 has-text-primary is-size-6">
          
          <div class="is-size-4 title has-text-weight-bold mb-4"> Hotel Policies:</div>
          <p>
            <span class="title is-size-6 has-text-weight-bold">Check In Time:</span>
            {{
              checkinTime
            }}
          </p>
          <p>
            <span class="title is-size-6 has-text-weight-bold">Check Out Time:</span>
            {{ checkoutTime }}

          </p>
          <p>
            <span class="title is-size-6 has-text-weight-bold">Stay Requirement:</span>
            {{ stayRequirement }}
          </p>
          <p>
            <span class="title is-size-6 has-text-weight-bold">Accepted Payments:</span>
            {{ acceptedPayments }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import HotelImages from "@/components/BookTravel/Hotel/HotelImages.vue"
import DateSelectorRange from "@/components/DateComponents/DateSelectorRange.vue"
import GMap from "@/components/GMap/GMap.vue"
import StayRoom from "@/components/BookTravel/Hotel/NewRoom.vue"
import RoomSkeleton from "@/components/BookTravel/Hotel/RoomSkeleton.vue"
import { mapState, mapActions } from "vuex"
import { DateTime } from "luxon"
const moment = require("moment")
export default {
  components: {
    DateSelectorRange,
    HotelImages,
    StayRoom,
    RoomSkeleton,
    GMap,
  },
  props: {
    hotelCode: String,
  },
  data() {
    return {
      loading: true,
      loadingRooms: true,
      hotel: {},
      description: {},
      newSearchDates: [],
      travelerRateCodes: [],
      sessionKey: null,
      showMapModal: false,
      governmentRate: null,
    }
  },
  computed: {
    ...mapState({
      searchDates: (state) => state.hotelSearch.searchDates,
      profile: (state) => state.travellersAndTrips.currentTravellerProfile,
      lastViewedHotel: (state) => state.hotelSearch.lastViewedHotel,
      selectedHotelRates: (state) => state.hotelSearch.selectedHotelRates,
      tripType: (state) => state.travellersAndTrips.travellers.tripType,
    }),
    cacheKey() {
      return this.$route.query.key
    },
    checkinTime() {
      const checkinTime = this.description.policy?.checkInTime
      if (!checkinTime) return ""

      return DateTime.fromFormat(checkinTime,
        "HH:mm:ss"
      ).toFormat("h:mm a")
    },
    checkoutTime() {
      const checkoutTime = this.description.policy?.checkOutTime
      if (!checkoutTime) return ""

      return DateTime.fromFormat(checkoutTime,
        "HH:mm:ss"
      ).toFormat("h:mm a")
    },
    detailImages() {
      const images = this.description?.imagesAndDescriptions?.hotel?.images?.filter(
        (i) => {
          return i.url && i.url.includes("http")
        }
      )
      return images?.slice(0, 30) || []
    },
    amenities() {
      return this.description?.amenities || []
    },
    policy() {
      return this.description?.policy || []
    },
    stayRequirement() {
      return this.policy.stayRequirement?._ || ""
    },
    hotelAddress() {
      return {
        addressLine:
          this.hotel?.address?.AddressLine ||
          this.description?.contactInfo?.address?.addressLine,
        chainName: this.hotel?.chainName,
        city:
          this.hotel?.address?.CityName ||
          this.description?.contactInfo?.address?.city,
        state:
          this.hotel?.address?.State ||
          this.description?.contactInfo?.address?.state,
        postalCode:
          this.hotel?.address?.PostalCode ||
          this.description?.contactInfo?.address?.postalCode,
      }
    },
    checkinDate() {
      return moment(this.newSearchDates[0]).format("YYYY-MM-DD")
    },
    checkoutDate() {
      return moment(this.newSearchDates[1]).format("YYYY-MM-DD")
    },
    hotelName() {
      return this.hotel?.hotelName || this.description?.hotelData?.hotelName
    },
    acceptedPayments() {
      if (!this.description?.policy?.acceptedPayments) return ""
      return this.description.policy.acceptedPayments.join(", ")
    },
    filteredRooms() {
      let roomGroup
      let rooms = []

      if (this.hotel?.roomsByCode?.length > 0) {
        roomGroup = this.hotel?.roomsByCode
      } else if (this.hotel?.roomsByType?.length > 0) {
        roomGroup = this.hotel?.roomsByType
      } else {
        return []
      }

      //group rates for a particular room code or room type
      for (let room of roomGroup) {
        let r = {
          roomCode: room.roomCode,
          roomType: room.roomType,
          commonDescriptions: room.commonDescriptions,
          roomImages: this.getRoomImages(room.roomCode),
        }

        r.rates = room.rates

        r.rates.sort((a, b) => {
          return (
            a.requestedCurrencyPricing.dailyRate -
            b.requestedCurrencyPricing.dailyRate
          )
        })

        // Find all refundable rates
        r.refundableRates = r.rates.filter((rate) => {
          return rate?.nonrefundable === false
        })

        //Find the lowest rate for each refund deadline
        let lowestRatesByDeadline = {}
        for (let rate of r.refundableRates) {
          let deadline = this.formatDeadline(rate.cancelPenalties[0]?.deadline)
          if (deadline) {
            if (
              !lowestRatesByDeadline[deadline] ||
              rate.requestedCurrencyPricing.dailyRate <
                lowestRatesByDeadline[deadline].requestedCurrencyPricing
                  .dailyRate
            ) {
              lowestRatesByDeadline[deadline] = rate
            }
          }
        }

        r.lowestCancelableRates = Object.values(lowestRatesByDeadline)

        //Find all non-refundable rates
        let nonRefRates = r.rates.filter((rate) => {
          return rate?.nonrefundable === true
        })

        //Find the lowest non-refundable rate
        let lowestNonRefRate = null
        if (nonRefRates.length > 0) {
          lowestNonRefRate = nonRefRates.reduce((lowest, current) => {
            return current.requestedCurrencyPricing.dailyRate <
              lowest.requestedCurrencyPricing.dailyRate
              ? current
              : lowest
          })
        }

        r.nonRefRates = nonRefRates
        r.lowestNonRefRate = lowestNonRefRate

        rooms.push(r)
      }

      //Sort rooms by lowest rate
      rooms.sort((a, b) => {
        return (
          a.rates[0]?.requestedCurrencyPricing.dailyRate -
          b.rates[0]?.requestedCurrencyPricing.dailyRate
        )
      })

      return rooms
    },
  },
  methods: {
    backNavigation() {
      if (this.pathMixin_isMeeting) {
        this.$router.push({
          name: "MeetingHotels",
          query: { key: this.cacheKey },
        })
      } else {
        this.$router.push({
          name: "BookingHotels",
          query: { key: this.cacheKey },
        })
      }
    },
    async getLiveRates() {
      this.loadingRooms = true

      let body = {
        stateful: true,
        sessionKey: null, //TODO pull from profile
        normalizeResponse: true,
        travelerContactId: this.profile.id,
        travelPolicyId: this.profile.travelPolicyId,
        tripType: this.tripType,
        hotelCode: this.hotelCode,
        infoToReturn: [
          "generalInfo",
          "guestRooms",
          "meetingRooms",
          "restaurants",
          "policies",
          "referencePoints",
          "attractions",
          "recreations",
          "loyaltyPrograms",
          "awards",
          "contactInformation",
          "multimedia",
        ],
        checkInDate: this.checkinDate,
        checkOutDate: this.checkoutDate,
        roomCount: 1,
        guestCount: 1,
        ratePlanCodes: this.selectedHotelRates && this.selectedHotelRates.length > 0 
          ? this.selectedHotelRates 
          : this.travelerRateCodes,
      }

      let result = await this.$app.huddleLabsServerCall(
        "amadeus/hotel/live-search",
        body
      )

      //result.data = [] // ← TEST: Force empty response to simulate failure

      this.sessionKey = result.meta.sessionKey

      // Get government rates from meta
      let governmentRates = result.meta?.governmentRates

      // Get rate for hotel's postal code
      if (governmentRates) {
        this.governmentRate = governmentRates[this.hotelAddress.postalCode]
      }

      this.loadingRooms = false
      if (result.data && result.data.length > 0) {
        this.hotel = result.data[0]
        this.description = this.hotel.description
        this.hotel.rooms.sort((a, b) =>
          a.enhancedPricing?.roomTypeCode?.localeCompare(
            b.enhancedPricing?.roomTypeCode
          )
        )
      } else if (result.meta.hotelInfo) {
        this.description = result.meta.hotelInfo
      } else if (result.errors && result.errors.length > 0) {
        let toastMessageOptions = {
          severity: "error",
          summary: "Error",
          detail: this.parseErrors(result.errors)[0],
          //life: 5000
        }
        //toast.add(toastMessageOptions)
      }
    },
    async getDescription() {
      // Implement the getDescription method
    },
    formatDeadline(deadline) {
      return DateTime.fromISO(deadline).toISODate()
    },
    parseErrors(errors) {
      let errorMessages = []
      for (let error of errors) {
        if (error.OTA_ERR) errorMessages.push(error.OTA_ERR)
        else if (error.title) errorMessages.push(error.title)
      }
      return errorMessages
    },
    preloadImages(images) {
      for (let i = 0; i < images.length; ++i) {
        let img = new Image()
        img.src = images[i].url
      }
    },
    getRoomImages(code) {
      let matchingRoom = this.description?.rooms?.find((room) => {
        return room.roomCode === code
      })

      return matchingRoom?.roomImages
    },
  },
  watch: {
    detailImages: {
      handler(newImages) {
        if (newImages.length > 0) {
          this.preloadImages(newImages)
        }
      },
      immediate: true,
    },
  },
  created() {
    const today = new Date()
    const tomorrow = new Date(Date.now() + 3600 * 1000 * 24)

    this.newSearchDates = [
      moment(this.searchDates[0] ?? today).toDate(),
      moment(this.searchDates[1] ?? tomorrow).toDate(),
    ]

    if (!this.hotelCode) {
      this.hotelCode = this.lastViewedHotel ?? null
    }

    const savedHotel = this.$store.getters["hotelSearch/findHotel"](
      this.hotelCode
    )

    if (savedHotel) {
      this.hotel = savedHotel
      // Clear any cached room data to enure we don't display stale data  
      this.hotel = {
        ...savedHotel,
        roomsByCode: undefined,
        roomsByType: undefined,
        rooms: undefined
      }
      this.loading = false
    }
  },
  mounted() {

    // Log the event
    let eventData = {
      event_type: 'Item Details Viewed',
      event_properties: {
        item_type: 'hotel',
        property_code: this.hotelCode,
        is_meeting_mode: this.pathMixin_isMeeting,
      }
    }
      
    this.$app.logEvent(eventData);

    this.getLiveRates().then(() => {
      this.loading = false
      this.loadingRooms = false
    })
  },
}
</script>

<style scoped>
/* Add any scoped styles here */
.container.main {
}

/* Mobile optimizations */
@media screen and (max-width: 1023px) {
  .container.main {
    padding: 1rem;
  }
}

.sticky-top {
  position: sticky;
  position: -webkit-sticky;
  z-index: 100;
  top: 90px;
}

.back-navigation {
  border-radius: 10px;
  width: fit-content;
  font-weight: 700;
}

.government-rate-notification {
  border-left: 4px solid #48c774;
  background-color: #ffffff;
  box-shadow: 0 2px 3px rgba(10, 10, 10, 0.1);
}

.rate-amount {
  color: #48c774;
  font-weight: 700;
  font-size: 1.1rem;
}
</style>
